import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import { styleMap } from 'lit/directives/style-map.js';

import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy, type PowerupState } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

const POWERUP_LABELS: Record<PowerupState['type'], string> = {
  shield: 'Shield',
  inferno: 'Inferno',
};

/**
 * Powerup Bar - Countdown progress bar for Shield/Inferno powerups
 *
 * Positioned at 70% vertical, centered. Shows a horizontal bar that
 * shrinks from 100% to 7% over the powerup duration via a CSS animation.
 * Game code manages the timer; this component only renders state.
 *
 * @element powerup-bar
 */
@customElement('powerup-bar')
export class PowerupBar extends LitElement {
  static override styles = [
    tokens,
    css`
      /* No z-index, as in legacy: DOM order paints the touch controls over
         the bar. On a short portrait screen it meets the cruise and DROP
         buttons, and at z 10 it covered them. Taps always reached the
         buttons (the bar is click-through); now the buttons stay visible. */
      :host {
        display: block;
        position: absolute;
        left: 50%;
        top: 70%;
        margin-left: -100px;
        width: 200px;
        height: 0;
        overflow: visible;
        /* Declared here rather than inherited from the body — the component
           owns its own typography. */
        font-family: var(--font-family);
        user-select: none;
        pointer-events: none;
      }

      .powerup {
        position: relative;
        width: 100%;
        height: 36px;
        background-color: var(--color-bg-selector);
        border-radius: var(--radius-lg);
        box-shadow: 0 0 2px 1px rgba(0, 0, 0, 0.3);
      }

      /* Drains over the powerup's duration (--drain-ms, set per powerup).
         keyed() gives each powerup a fresh element, so a new pickup restarts
         the drain from full. */
      .percent {
        position: absolute;
        width: 100%;
        height: 36px;
        left: 0;
        top: 0;
        border-radius: var(--radius-lg);
        box-shadow: inset 0 -4px 0 0 rgba(0, 0, 0, 0.15);
        animation: powerup-drain var(--drain-ms) linear forwards;
      }

      @keyframes powerup-drain {
        from {
          width: 100%;
        }
        to {
          width: 7%;
        }
      }

      .percent.shield {
        background-color: #787878;
      }

      .percent.inferno {
        background-color: #b21313;
      }

      .name {
        position: absolute;
        height: 36px;
        line-height: 36px;
        font-size: var(--font-size-16);
        font-weight: bold;
        color: var(--color-text-white);
        text-shadow: var(--shadow-text);
        box-sizing: border-box;
        padding-left: 12px;
      }

      /* Phones, portrait or landscape: legacy's query, which game-mode-status
         shares. A width-only 480px query had left landscape phones on the
         full-size bar. */
      @media only screen and (max-width: 599px) and (orientation: portrait),
        only screen and (max-height: 599px) and (orientation: landscape) {
        :host {
          transform: scale(0.6);
        }
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  override render() {
    const powerup = this.uiState.activePowerup;
    if (!powerup) return nothing;

    return keyed(
      powerup.id,
      html`
        <div class="powerup">
          <div class="percent ${powerup.type}" style=${styleMap({ '--drain-ms': `${powerup.durationMs}ms` })}></div>
          <div class="name">${POWERUP_LABELS[powerup.type]}</div>
        </div>
      `,
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'powerup-bar': PowerupBar;
  }
}
